import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { GolfAuthNavLogo } from '@golfauth/ui';

/**
 * MarketingNav — the sticky top nav shared by the premium marketing
 * pages (HomePage `/` and ProPage `/pro`). Both render inside a `.mkt`
 * wrapper, so it uses the `.mkt nav.bar` design-system styling from
 * marketingRedesign.css.
 *
 * Responsive behaviour:
 *   - Desktop (> 820px): the full `.navlinks` row (links + primary CTA).
 *   - Mobile (<= 820px): `.navlinks` is hidden and a hamburger toggles a
 *     full-width `.navdrawer` with the same links stacked, plus the CTA.
 *     Before this, the inline row wrapped on phones (the pill collapsed
 *     into a circle, the CTA wrapped to two lines) and the section links
 *     were simply unreachable.
 *
 * Links are same-page anchors (`href="#pricing"`) or route links (`to`).
 * A `pill` link renders as the bordered `.nl-pro` chip on desktop; in the
 * drawer every link renders as a plain stacked row.
 *
 * `brandVariant="pro"` shows the shared GolfAuthNavLogo Pro badge (the
 * little blue PRO box) and lands the wordmark on `/pro`. Always pass
 * `surface="dark"` — `.mkt nav.bar` is a dark gradient regardless of
 * the document light/dark class, so the theme-auto wordmark would
 * render the light asset on a dark bar and lose contrast.
 */
export type MarketingNavLink =
  | { label: string; to: string; kind?: 'text' | 'pill' }
  | { label: string; href: string; kind?: 'text' | 'pill' };

export interface MarketingNavCta {
  label: string;
  to?: string;
  onClick?: () => void;
}

function NavAnchor({
  link,
  className,
  onNavigate,
}: {
  link: MarketingNavLink;
  className?: string;
  onNavigate?: () => void;
}) {
  if ('href' in link) {
    return (
      <a href={link.href} className={className} onClick={onNavigate}>
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.to} className={className} onClick={onNavigate}>
      {link.label}
    </Link>
  );
}

export default function MarketingNav({
  links,
  cta,
  brandVariant = 'default',
}: {
  links: MarketingNavLink[];
  cta: MarketingNavCta;
  brandVariant?: 'default' | 'pro';
}) {
  const [open, setOpen] = useState(false);
  const isPro = brandVariant === 'pro';
  const brandTo = isPro ? '/pro' : '/';

  const renderCta = (className: string) => {
    const close = () => setOpen(false);
    if (cta.to) {
      return (
        <Link to={cta.to} className={className} onClick={close}>
          {cta.label}
        </Link>
      );
    }
    return (
      <button
        type="button"
        className={className}
        onClick={() => {
          close();
          cta.onClick?.();
        }}
      >
        {cta.label}
      </button>
    );
  };

  return (
    <nav className="bar">
      <div className="wrap row">
        <Link
          to={brandTo}
          className="brand"
          aria-label={isPro ? 'GolfAuth Pro home' : 'GolfAuth home'}
          onClick={() => setOpen(false)}
        >
          <GolfAuthNavLogo
            className="brand-logo"
            variant={brandVariant}
            surface="dark"
          />
        </Link>

        {/* Desktop links + CTA */}
        <div className="navlinks">
          {links.map((link) => (
            <NavAnchor
              key={link.label}
              link={link}
              className={link.kind === 'pill' ? 'nl-pro' : undefined}
            />
          ))}
          {renderCta('btn nl-start')}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="navtoggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="navdrawer">
          {links.map((link) => (
            <NavAnchor
              key={link.label}
              link={link}
              className="nd-item"
              onNavigate={() => setOpen(false)}
            />
          ))}
          {renderCta('btn nd-cta')}
        </div>
      )}
    </nav>
  );
}
